/**
 * Wave 7 / Lot B — lecture d'une ressource du portail avec son ÉTAT réel.
 *
 * Avant, chaque page faisait `apiGet(...).then(setList).catch(setError)` : une
 * coupure réseau, un 403 « accès désactivé » et une liste réellement vide
 * rendaient le même écran gris. Ce hook nomme l'état, dans l'ordre imposé —
 * chargement, HORS-LIGNE, 401, 403, erreur — et n'accorde « ready » qu'à une
 * lecture qui a VRAIMENT abouti. Une page ne peut donc plus annoncer « aucune
 * facture » alors qu'elle n'a jamais réussi à en lire une.
 *
 * Le portail n'a pas de client de requêtes (pas de react-query) et n'en gagne
 * pas un ici : 40 lignes de `useEffect` suffisent et gardent le bundle du
 * portail aussi mince qu'il l'est aujourd'hui.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { PortalApiError } from "../api/portal-client";

export type PortalStatus =
  | "loading"
  | "offline"
  | "unauthorized"
  | "forbidden"
  | "error"
  | "ready";

export type PortalFailure = Exclude<PortalStatus, "loading" | "ready">;

/**
 * Signatures de `fetch()` quand la requête n'est JAMAIS partie — Chrome,
 * Safari/iOS, Firefox et undici, les quatre moteurs que le portail rencontre.
 */
const RESEAU_INJOIGNABLE =
  /failed to fetch|load failed|networkerror|network request failed|fetch failed|network connection was lost|connection appears to be offline/i;

/**
 * Ordre de classement NON négociable : hors-ligne d'abord (un client en camion
 * de livraison perd le réseau plus souvent que ses droits), puis 401 et 403 —
 * qui ne veulent pas dire la même chose au client : session à refaire vs accès
 * coupé par son fournisseur — et enfin l'erreur générique.
 */
export function classifyPortalError(error: unknown): PortalFailure {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return "offline";
  if (error instanceof PortalApiError) {
    if (error.status === 401) return "unauthorized";
    if (error.status === 403) return "forbidden";
    return "error";
  }
  // `fetch()` rejette en TypeError quand la requête n'est jamais partie : Wi-Fi
  // coupé, DNS muet, API injoignable. `navigator.onLine` ne voit que le lien
  // local et reste `true` dans ces cas-là — il ne suffit pas à lui seul.
  //
  // W7-C — mais le MESSAGE est exigé : `TypeError` est aussi ce que lève un bug
  // du portail (JSON illisible, champ absent, `x.map` sur autre chose qu'un
  // tableau). Tout TypeError valait « hors ligne » : le client partait vérifier
  // une connexion qui marchait, et l'écran cachait une vraie panne derrière un
  // faux diagnostic. En cas de doute on dégrade vers l'erreur générique — elle,
  // au moins, propose une reprise.
  if (error instanceof TypeError && RESEAU_INJOIGNABLE.test(error.message)) return "offline";
  return "error";
}

export interface PortalResource<T> {
  data: T | null;
  status: PortalStatus;
  /** Message renvoyé par l'API, quand elle en donne un (403 « portail coupé »). */
  error: string | null;
  reload: () => void;
}

export function usePortalResource<T>(fetcher: () => Promise<T>): PortalResource<T> {
  const [data, setData] = useState<T | null>(null);
  const [status, setStatus] = useState<PortalStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  // Le fetcher est presque toujours une lambda inline : le garder dans une ref
  // évite de relancer la requête à chaque rendu sans imposer un `useCallback`
  // à chaque page.
  const fetcherRef = useRef(fetcher);
  useEffect(() => {
    fetcherRef.current = fetcher;
  });

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setError(null);
    fetcherRef
      .current()
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setStatus("ready");
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setStatus(classifyPortalError(e));
        setError(e instanceof PortalApiError ? e.message : null);
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  return { data, status, error, reload };
}
